import { Box, TagsInput, TextInput } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReminderApi } from "../../apis/reminder";
import { message } from "../../utils/Message";
import { countChanges, MasterCard, OffCard, SaveBar, SectionCard, SettingRow, settingsClasses, SwitchRow } from "./settings-ui";
import type { SettingsTabProps } from "./settings-ui";
import { useReminderConfigData } from "./use-settings-config";

interface IReminderConfig {
  reminderEnabled: boolean;
  preDueRemindersEnabled: boolean;
  preDueReminderDays: string[];
  postDueRemindersEnabled: boolean;
  postDueReminderDays: string[];
  includePaymentLink: boolean;
  includeInvoicePdf: boolean;
  includeLateFeeInfo: boolean;
  ccSellerManager: boolean;
  sellerManagerEmail: string;
}

const initialConfig: IReminderConfig = {
  reminderEnabled: false,
  preDueRemindersEnabled: false,
  preDueReminderDays: [],
  postDueRemindersEnabled: false,
  postDueReminderDays: [],
  includePaymentLink: false,
  includeInvoicePdf: false,
  includeLateFeeInfo: false,
  ccSellerManager: false,
  sellerManagerEmail: "",
};

const fromServer = (data: any): IReminderConfig => ({
  reminderEnabled: data.reminderEnabled ?? false,
  preDueRemindersEnabled: data.preDueRemindersEnabled ?? false,
  preDueReminderDays: (data.preDueReminderDays ?? []).map(String),
  postDueRemindersEnabled: data.postDueRemindersEnabled ?? false,
  postDueReminderDays: (data.postDueReminderDays ?? []).map(String),
  includePaymentLink: data.includePaymentLink ?? false,
  includeInvoicePdf: data.includeInvoicePdf ?? false,
  includeLateFeeInfo: data.includeLateFeeInfo ?? false,
  ccSellerManager: data.ccSellerManager ?? false,
  sellerManagerEmail: data.sellerManagerEmail ?? "",
});

export function ReminderConfig({ onDirtyChange }: SettingsTabProps) {
  const { t } = useTranslation("settings");
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<IReminderConfig>(initialConfig);
  const [saved, setSaved] = useState<IReminderConfig>(initialConfig);

  const { data, isLoading, mutate } = useReminderConfigData();

  useEffect(() => {
    const server = data?.config;
    if (!server) return;
    const next = fromServer(server);
    setConfig(next);
    setSaved(next);
  }, [data]);

  const handleChange = (field: keyof IReminderConfig, value: any) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...config,
        preDueReminderDays: config.preDueReminderDays.map(Number).filter((n) => !isNaN(n)),
        postDueReminderDays: config.postDueReminderDays.map(Number).filter((n) => !isNaN(n)),
      };
      await ReminderApi.updateConfig(payload);
      setSaved(config);
      await mutate();
      message.success(t("reminder.saveSuccess"));
    } catch (err: any) {
      message.error(err?.message || t("reminder.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const changes = countChanges(config, saved);
  useEffect(() => onDirtyChange(changes), [changes, onDirtyChange]);
  useEffect(() => () => onDirtyChange(0), [onDirtyChange]);

  return (
    <Box className={isLoading ? settingsClasses.loading : undefined}>
      <Box className={settingsClasses.main}>
        {!config.reminderEnabled ? (
          <OffCard
            title={t("reminder.off.title")}
            description={t("reminder.off.description")}
            enableLabel={t("reminder.off.enable")}
            onEnable={() => handleChange("reminderEnabled", true)}
            features={[
              { title: t("reminder.off.featurePre"), description: t("reminder.off.featurePreText") },
              { title: t("reminder.off.featurePost"), description: t("reminder.off.featurePostText") },
              { title: t("reminder.off.featureCc"), description: t("reminder.off.featureCcText") },
            ]}
          />
        ) : (
          <>
            <MasterCard
              title={t("reminder.master.title")}
              subtitle={t("reminder.master.subtitle")}
              checked={config.reminderEnabled}
              onChange={(v) => handleChange("reminderEnabled", v)}
            />

            <SectionCard title={t("reminder.section.preDue")} subtitle={t("reminder.section.preDueSubtitle")}>
              <SwitchRow
                label={t("reminder.activeSwitch")}
                checked={config.preDueRemindersEnabled}
                onChange={(v) => handleChange("preDueRemindersEnabled", v)}
              />
              <SettingRow label={t("reminder.preDueDays.label")} description={t("reminder.preDueDays.description")}>
                <TagsInput
                  aria-label={t("reminder.preDueDays.label")}
                  placeholder={t("reminder.preDueDays.placeholder")}
                  value={config.preDueReminderDays}
                  onChange={(v) => handleChange("preDueReminderDays", v)}
                  disabled={!config.preDueRemindersEnabled}
                  size="sm" radius="md"
                />
              </SettingRow>
            </SectionCard>

            <SectionCard title={t("reminder.section.postDue")} subtitle={t("reminder.section.postDueSubtitle")}>
              <SwitchRow
                label={t("reminder.activeSwitch")}
                checked={config.postDueRemindersEnabled}
                onChange={(v) => handleChange("postDueRemindersEnabled", v)}
              />
              <SettingRow label={t("reminder.postDueDays.label")} description={t("reminder.postDueDays.description")}>
                <TagsInput
                  aria-label={t("reminder.postDueDays.label")}
                  placeholder={t("reminder.postDueDays.placeholder")}
                  value={config.postDueReminderDays}
                  onChange={(v) => handleChange("postDueReminderDays", v)}
                  disabled={!config.postDueRemindersEnabled}
                  size="sm" radius="md"
                />
              </SettingRow>
            </SectionCard>

            <SectionCard title={t("reminder.section.content")} subtitle={t("reminder.section.contentSubtitle")}>
              <SwitchRow label={t("reminder.includePaymentLink")} checked={config.includePaymentLink} onChange={(v) => handleChange("includePaymentLink", v)} />
              <SwitchRow label={t("reminder.includeInvoicePdf")} checked={config.includeInvoicePdf} onChange={(v) => handleChange("includeInvoicePdf", v)} />
              <SwitchRow label={t("reminder.includeLateFeeInfo")} checked={config.includeLateFeeInfo} onChange={(v) => handleChange("includeLateFeeInfo", v)} />
            </SectionCard>

            <SectionCard title={t("reminder.section.manager")} subtitle={t("reminder.section.managerSubtitle")}>
              <SwitchRow label={t("reminder.ccManager")} checked={config.ccSellerManager} onChange={(v) => handleChange("ccSellerManager", v)} />
              {config.ccSellerManager && (
                <SettingRow label={t("reminder.managerEmail")}>
                  <TextInput
                    aria-label={t("reminder.managerEmail")}
                    placeholder="manager@example.com"
                    value={config.sellerManagerEmail}
                    onChange={(e) => handleChange("sellerManagerEmail", e.currentTarget.value)}
                    size="sm" radius="md"
                  />
                </SettingRow>
              )}
            </SectionCard>
          </>
        )}

        <SaveBar count={changes} saving={saving} onSave={handleSave} onCancel={() => setConfig(saved)} />
      </Box>
    </Box>
  );
}
